import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  SearchIcon,
  ViewListIcon,
  ViewGridIcon,
  ExternalLinkIcon
} from '@heroicons/react/outline';
import { 
  PencilAltIcon, 
  TrashIcon
} from '@heroicons/react/solid';
import toastService from '../../../../services/toast';
import AddGroupModal from './components/AddGroupModal';
import EditGroupModal from './components/EditGroupModal';
import DeleteGroupModal from './components/DeleteGroupModal';

const GroupsPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([
    {
      id: 1,
      title: 'مجموعة Python للمبتدئين',
      course: 'مبادئ البرمجة بلغة Python',
      type: 'telegram',
      url: 'https://t.me/python_beginners',
      status: 'active'
    },
    {
      id: 2,
      title: 'مجموعة تصميم واجهات المستخدم',
      course: 'تصميم واجهات المستخدم',
      type: 'whatsapp',
      url: 'https://chat.whatsapp.com/example',
      status: 'inactive'
    }
  ]);
  const [viewType, setViewType] = useState('table');

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleAddGroup = () => {
    setShowAddModal(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  const handleDeleteGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleVisitGroup = (url) => {
    window.open(url, '_blank');
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         group.course.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="p-6">
      {/* بطاقة العنوان والبحث والأيقونات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة مجموعات الدورات</h2>
          <div className="flex gap-4">
            <div className="relative group">
              <PlusIcon 
                className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                onClick={handleAddGroup}
              />
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                إضافة مجموعة جديدة
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن مجموعة</label>
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              </div>
              <input
                type="text"
                placeholder="البحث عن مجموعة..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="block w-full pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* بطاقة جدول المجموعات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">المجموعات</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-md ${viewType === 'table' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                <ViewListIcon className="h-5 w-5 text-gray-500" />
              </button>
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-md ${viewType === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                <ViewGridIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {viewType === 'table' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      اسم المجموعة
                    </th>
                    <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      الدورة
                    </th>
                    <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      النوع
                    </th>
                    <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      الحالة
                    </th>
                    <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredGroups.map((group) => (
                    <tr key={group.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {group.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {group.course}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {group.type === 'telegram' ? 'تليجرام' : 'واتساب'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          group.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        }`}>
                          {group.status === 'active' ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleVisitGroup(group.url)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <ExternalLinkIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEditGroup(group)}
                            className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                          >
                            <PencilAltIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <div key={group.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">{group.title}</h4>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{group.course}</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      group.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                    }`}>
                      {group.status === 'active' ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {group.type === 'telegram' ? 'تليجرام' : 'واتساب'}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleVisitGroup(group.url)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <ExternalLinkIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEditGroup(group)}
                        className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                      >
                        <PencilAltIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* نوافذ الإضافة والتعديل والحذف */}
      {showAddModal && (
        <AddGroupModal
          onClose={() => setShowAddModal(false)}
          onAdd={(newGroup) => {
            setGroups([...groups, { ...newGroup, id: Date.now() }]);
            setShowAddModal(false);
            toastService.success('تم إضافة المجموعة بنجاح');
          }}
        />
      )}

      {showEditModal && (
        <EditGroupModal
          group={selectedGroup}
          onClose={() => setShowEditModal(false)}
          onEdit={(editedGroup) => {
            setGroups(groups.map(g => g.id === editedGroup.id ? editedGroup : g));
            setShowEditModal(false);
            toastService.success('تم تحديث المجموعة بنجاح');
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteGroupModal
          group={selectedGroup}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            setGroups(groups.filter(g => g.id !== selectedGroup.id));
            setShowDeleteModal(false);
            toastService.success('تم حذف المجموعة بنجاح');
          }}
        />
      )}
    </div>
  );
};

export default GroupsPage;
